/**
 * Types for the global search page (`components/Search/`) and, despite the name, the catalog's
 * per-row product state (`ProductsListState`, used by `components/Catalog/`).
 *
 * The search response comes from Items API global search v2 (`APIUrl.GLOBAL_SEARCH_V2`, called by
 * `components/Search/Helpers/HelperFunctions.tsx`). Several members here have no importer and
 * are marked on the symbol. `ActionType` and `UnitOfMeasureType` repeat members of
 * `enums/common.enums.ts`.
 */

import { Language } from "@ryerson/frontend.application";
import { RefObject } from "react";
import { ShipToAddress, SalesPlantInformation } from "@ryerson/frontend.common";
import { DeliveryTimesRequestParams } from "@components/Helper/leadTimesHelpers";
import {
	Attribute,
	CurrencyCode,
	ItemSearchSummary,
	MatchingProduct,
	Processing,
} from "@ryerson/frontend.common";
import { UpdatedMatchingProduct } from "@components/Search/Helpers/Search.models";

/** Items API global search v2 response (`result-type=detailed`). */
export interface GlobalSearchResultsResponse {
	articlesResources: Array<ArticlesResourcesObject>;
	matchingProducts: Array<MatchingProduct>;
	recentlyPurchased: Array<MatchingProduct>;
	megaMenuProducts: Array<MatchingMegaMenuProductsModel>;
}

// data returned by cut modal (shared component)
/**
 * Not imported from here: the define-cut callers use the `DefineCutResponse` exported by
 * `components/Shared/DefineCutModal/DefineCutModal.tsx`.
 */
export type DefineCutResponse = {
	isResponseUpdated: boolean;
	index: number;
	ryersonSku: string;
	defineCutResponse: Processing;
};

/** Not referenced; the search page uses {@link SearchViewOption}. */
export enum PageViewMode {
	list = "list",
	table = "table",
}

/** Not referenced. */
export interface AttributeNoLocal {
	name: string;
	value: string | number;
}

/** Not referenced. */
export interface MatchingCategoriesModel {
	id: string;
	mageniumId: string;
	product: string;
	form: string;
	shape: string;
}

/** A product-category link from the site mega menu that matched the search phrase. */
export interface MatchingMegaMenuProductsModel {
	linkUrl: string;
	megaMenuLinkLabel: string;
	productCategoryLabel: string;
	productGroupLabel: string;
	description: string;
}
// NOTE: TODO Refactor Instances to use cental UOM ENUM in COMMON

/** Not imported from here (same members as `enums/common.enums.ts` → `ActionType`). */
export enum ActionType {
	INCREMENT = "increment",
	DECREMENT = "decrement",
}
/**
 * `type` of a Prices API pricing request. Search sends `Default`; the Prices API prices
 * `ShoppingCart` lines from the ERP (`web-architecture/services.md`, Prices).
 */
export enum PricingRequestType {
	Default = "Default",
	ShoppingCart = "ShoppingCart",
}

/**
 * Which UOM selector of a search row changed. Same members as `enums/common.enums.ts` →
 * `UnitOfMeasureType`.
 */
export enum UnitOfMeasureType {
	QUANTITY = "quantityUom",
	PRICE = "priceUom",
}

/**
 * Which field of a search result row's state an update targets
 * (`Search/ListView/ProductSection.tsx`).
 */
export enum StateRef {
	"priceUOM" = "priceUom",
	"qtyUOM" = "quantityUom",
	"quantity" = "quantity",
}

/** Which bulk default a UOM dropdown sets: the quantity UOM or the pricing UOM. */
export enum UomDefaultEnum {
	Quantity = "isQuantityDefault",
	Pricing = "isPricingDefault",
}

/**
 * Client-side state of one product row in the search results and the catalog: selection,
 * expansion, chosen quantity and UOMs, and any defined cut. `materialMaster` is the SKU sent to
 * pricing and delivery-time calls.
 */
export type ProductsListState = {
	id: string;
	index: number;
	altPartNumber?: string;
	materialMaster: string;
	checked: boolean;
	expanded: boolean;
	quantity: number;
	qtyUom: string;
	priceUom: string;
	pricePerPound: number;
	defaultWeight: number;
	showExtendedPrice: boolean;
	cutFlag: string;
	definedCut: Processing;
	isResetToDefault?: boolean;
};

/**
 * One line of an Items API delivery-times request built by the search page
 * (`APIUrl.DELIVERY_TIMES`).
 */
export type DeliveryTimesRequestItems = {
	qty: number;
	qtyUOM: string;
	priceUOM: string;
	comboKey: string;
	id: number;
	materialMaster: string;
	customerPartNo: string;
};

/**
 * Not referenced. `shippingType` is `RC` (standard delivery) or `WC` (customer pickup), as in
 * `@ryerson/common.enums` → `ShippingType`.
 */
export type DeliveryTimesRequestBody = {
	couponCode: string;
	currency: string;
	excludeDeliveryDate: boolean;
	lineItems: DeliveryTimesRequestItems[] | DeliveryTimesRequestParams[];
	partners: SalesPlantInformation | ShipToAddress;
	shippingType: "WC" | "RC";
};

/** Props of the price display in a search list row (`Search/ListView/ProductSection.tsx`). */
export type CurrentPriceProps = {
	primaryBrand: string;
	isFetching: boolean;
	isPriceChanged: boolean;
	convertedPriceOfProduct: number;
	currencyType: CurrencyCode | undefined;
};

/** A notification a search row passes to `addNotification` (the add-to-cart toast). */
export declare type Message = {
	id?: number;
	type: "error" | "success" | "warning" | "info" | "add-to-cart" | "remove-from-cart";
	timeOut?: number;
	message: JSX.Element;
};

/** Search results layout: list view or table view. */
export enum SearchViewOption {
	List = "list",
	Table = "table",
}

// Used for passing values to define cut modal component (`Search/Context/SearchContext.tsx`)
export type DefineCutModalProps = {
	index: number;
	defineCutData: Processing;
	product: UpdatedMatchingProduct;
};

// Begin Articles and Resources

/** One article, location, event or news entry in the search response's `articlesResources`. */
export interface ArticlesResourcesObject {
	link: string;
	image: string;
	title: string;
	excerpt: string;
	category: ArticleCategory;
	blogCategory: string;
}

/** `category` values of an articles-and-resources entry; `Search.tsx` splits the tabs by them. */
export enum ArticleCategory {
	"blog" = "blog",
	"location" = "location",
	"event" = "event",
	"news" = "news",
}
/** Not referenced. */
export enum BadgeIcon {
	"location" = "location-marker-outline",
	"event" = "calendar",
	"news" = "news",
	"default" = "default",
}

/** `blogCategory` values of a blog entry. */
export enum BlogCategories {
	"Market Updates" = "Market Updates",
	"Grade Anatomy" = "Grade Anatomy",
	"Metal Processing" = "Metal Processing",
	"Industry Trends" = "Industry Trends",
	"Metal Perspective" = "Metal Perspective",
	"Success Stories" = "Success Stories",
}
/**
 * Badge colour per blog category, as a `<palette>.<colour>` path into the theme's colours
 * (`ArticlesResourcesPage.tsx` → `toColor`); an unlisted category gets `"Fallback Color"`.
 */
export enum BlogCategoryColors {
	"news" = "secondary.red",
	"locations" = "secondary.yellow",
	"Metal Processing" = "primary.gray",
	"Industry Trends" = "primary.darkerGray",
	"Metal Perspective" = "secondary.lightBlue",
	"Success Stories" = "secondary.green",
	"Market Updates" = "primary.gray",
	"Fallback Color" = "primary.gray",
}

/** Not referenced. */
export enum NewsCategories {
	"Ryerson Earnings" = "Ryerson Earnings",
	"Earnings Releases" = "Earnings Releases",
	"Ryerson Initiatives" = "Ryerson Initiatives",
	"Ryerson Quarter Results" = "Ryerson Quarter Results",
}

/** Not referenced. */
export enum NewsCategoryColors {
	"Ryerson Earnings" = "secondary.green",
	"Earnings Releases" = ".secondary.blue",
	"Ryerson Initiatives" = "secondary.red",
	"Ryerson Quarter Results" = "secondary.yellow",
}

/**
 * Placeholder image URL on the static-assets CDN, used when a search result has no image.
 * Resolved at build time: `NEXT_PUBLIC_URI_STATIC` is inlined by the static export.
 */
export const NO_IMAGE =
	(process.env.NEXT_PUBLIC_URI_STATIC ?? "") +
	"/static-assets/images/renderings/NoImageAvailable.png";
